import { DOCUMENT } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { Event, NavigationEnd, Router, RouterEvent, Scroll } from '@angular/router';
import { INgPopoverOptions, NgMessageToastAssistant } from '@libs/frameworks/angular';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, filter, map } from 'rxjs';

import { SparkSidebarNavigationService } from '../../../services/sidebar-navigation/sidebar-navigation.service';
import { ISparkSidebarCollapseChange, ISparkSidebarConfig, ISparkSidebarList, ISparkSidebarMenu, ISparkSidebarSubMenu } from './sidebar.interface';


@Component({
    selector: 'spark-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SparkSidebarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

	//** Configurations */
	readonly INFO_POPOVER_CONFIG: Partial<INgPopoverOptions> 	= {
		containerPosition	: 'body'
	};

	//** Input properties */
	@Input() config: ISparkSidebarConfig | null = null;

	//** Output properties */
	@Output() readonly disableLinkClicked: EventEmitter<string> = new EventEmitter<string>();
	@Output() readonly menuCollapseChange: EventEmitter<ISparkSidebarCollapseChange> = new EventEmitter<ISparkSidebarCollapseChange>();

	//** UI Variables */
	toggled$: Observable<boolean> = new Observable<boolean>();
	private readonly subscriptions: Subscription = new Subscription();
	private scrollFrame: number | null = null;
	private scrollRetryTimer: number | null = null;

	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly elementRef: ElementRef<HTMLElement>,
		private ngbModal				: NgbModal,
		private router					: Router,
		private cdr						: ChangeDetectorRef,
		private messageToastAssistant	: NgMessageToastAssistant,
		private sidebarNavigationService: SparkSidebarNavigationService
	) {}

	ngOnInit(): void {

		//0 - check for sidebar config
		if (!this.config) throw new Error(`SparkSidebarComponent => ngOnInit => FATAL ERROR: Sidebar config is not provided.`);

		//1 - get toggled observable
		this.toggled$ = this.sidebarNavigationService.getSidebarToggleObservable();

		//2 - detect router change event to close all modal
		this.subscriptions.add(this.router.events.pipe(
			map((event: Event) => (event as Scroll).routerEvent as RouterEvent),
			filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
		).subscribe((navigationEndEvent: NavigationEnd) => {
			this.onChangeEvent(navigationEndEvent);
		}));
	}

	ngOnChanges(): void {
		this.scheduleActiveRouteScroll();
	}

	ngAfterViewInit(): void {
		this.scheduleActiveRouteScroll();
	}

	ngOnDestroy(): void {
		const browserWindow: Window | null = this.document.defaultView;
		this.subscriptions.unsubscribe();
		if (!browserWindow) return;
		if (this.scrollFrame !== null) browserWindow.cancelAnimationFrame(this.scrollFrame);
		if (this.scrollRetryTimer !== null) browserWindow.clearTimeout(this.scrollRetryTimer);
	}


	/**------------------------------------------------------
	 * Sidebar Menu State
	 */
	toggleMenu(menu: ISparkSidebarMenu): void {
		menu.isCollapsed = !menu.isCollapsed;
		this.menuCollapseChange.emit({
			menuId		 : menu.id,
			isCollapsed : menu.isCollapsed
		});
	}


	/**------------------------------------------------------
	 * Router Change Event Handler
	 * > Hook for rout changed (triggered if another page is loaded)
	 * > Handle Sidebar selection and fb chat
	 */
	private onChangeEvent(event: NavigationEnd) {

		//0 - close all modals & toasts
		this.ngbModal.dismissAll();
		this.messageToastAssistant.closeAll();

		//1 - do nothing if no config is provided
		if (!this.config) return;

		//2 - setting the focus on the specific link
		const sidebarMenus	 : ISparkSidebarMenu[] 	  = this.config.pageLists.flatMap((sidebarList: ISparkSidebarList) => sidebarList.menus);
		const sidebarSubMenus: ISparkSidebarSubMenu[] = sidebarMenus.flatMap((sidebarMenu: ISparkSidebarMenu) => sidebarMenu.subMenus);
		const routePath: string = this.getSelectedRoutePath(event.urlAfterRedirects, sidebarSubMenus);
		for (const sidebarSubMenu of sidebarSubMenus) {
			sidebarSubMenu.isSelected = sidebarSubMenu.routePath === routePath;
		}
		this.cdr.markForCheck();
		this.scheduleActiveRouteScroll();
	}

	private scheduleActiveRouteScroll(): void {
		const browserWindow: Window | null = this.document.defaultView;
		if (!browserWindow) return;
		if (this.scrollFrame !== null) browserWindow.cancelAnimationFrame(this.scrollFrame);
		if (this.scrollRetryTimer !== null) browserWindow.clearTimeout(this.scrollRetryTimer);

		this.scrollFrame = browserWindow.requestAnimationFrame(() => {
			this.scrollFrame = null;
			this.scrollActiveRouteIntoView();
			this.scrollRetryTimer = browserWindow.setTimeout(() => {
				this.scrollRetryTimer = null;
				this.scrollActiveRouteIntoView();
			}, 350);
		});
	}

	private scrollActiveRouteIntoView(): void {
		const sidebarContent: HTMLElement | null = this.elementRef.nativeElement.querySelector('.sidebar-content');
		const activeLink: HTMLElement | null = sidebarContent?.querySelector('.sidebar-dropdown .sidebar-link.active') ?? null;
		if (!sidebarContent || !activeLink) return;

		const activeMenu: HTMLElement | null = activeLink.closest('.sidebar-nav > .sidebar-item');
		const stickyUser: HTMLElement | null = sidebarContent.querySelector('.sidebar-user');
		const contentRect: DOMRect = sidebarContent.getBoundingClientRect();
		const stickyUserRect: DOMRect | null = stickyUser?.getBoundingClientRect() ?? null;
		const visibleTop: number = Math.max(contentRect.top, stickyUserRect?.bottom ?? contentRect.top) + 12;
		const visibleBottom: number = contentRect.bottom - 12;
		const availableHeight: number = Math.max(0, visibleBottom - visibleTop);
		const targetElement: HTMLElement = activeMenu && activeMenu.offsetHeight <= availableHeight ? activeMenu : activeLink;
		const targetRect: DOMRect = targetElement.getBoundingClientRect();
		let scrollDelta: number = 0;

		if (targetRect.top < visibleTop) scrollDelta = targetRect.top - visibleTop;
		else if (targetRect.bottom > visibleBottom) scrollDelta = targetRect.bottom - visibleBottom;

		if (scrollDelta !== 0) sidebarContent.scrollTop += scrollDelta;
	}

	private getSelectedRoutePath(url: string, sidebarSubMenus: ISparkSidebarSubMenu[]): string {
		const routePath: string = url.split('?')[0].split('#')[0];
		if (!routePath.startsWith('/research/research/detail/')) return routePath;

		const source: string | null = this.router.parseUrl(url).queryParamMap.get('source');
		const sourceRoutePath: string = source === 'dashboard'
			? '/'
			: `/research/research/${source || 'best-sellers'}`;
		const availableRoutes: Set<string> = new Set(sidebarSubMenus.map((sidebarSubMenu: ISparkSidebarSubMenu) => sidebarSubMenu.routePath));

		return availableRoutes.has(sourceRoutePath) ? sourceRoutePath : '/research/research/best-sellers';
	}
}
